import React from "react";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import isequal from "lodash.isequal";
import {
  serviceCaller,
  ServiceCallerProps,
  ModalProps,
  withLoginRedirect,
} from "../../composition";
import PostItem from "../postItem";
import firebase from "firebase/compat/app";
import get from "lodash.get";
import { IPost, PostType } from "../../../types/services/IPost";
import IUser from "../../../types/services/IUser";
import * as commonActions from "../../../utils/actions/common";
import { ScaleLoader } from "react-spinners";
import ISortByType from "../../../types/services/ISortByType";
import Fragment from "../../../toolkit/fragment";
import ISegment from "../../../types/services/ISegment";
import { paginateDonors, getReindexStatus } from "../../../utils/fetch";
import { isDeliveredPostMediaUpdating } from "../../../utils/helpers";
import { UncontrolledAlert } from "reactstrap";
import { ContactsProcessingBannerNotice } from "../fileProcessing/importProcessingNotice";

interface Props extends ServiceCallerProps, ModalProps {
  firebase: firebase.app.App;
  updateShowMore: (show: boolean) => void;
  publishedPosts: Array<{
    key: string;
    value: IPost;
  }>;
  pendingPosts: Array<{
    key: string;
    value: IPost;
  }>;
  users: Array<{
    key: string;
    value: IUser;
  }>;
  postCount: number;
  hideSelection: () => void;
  sortBy: ISortByType;
  sortAsc: boolean;
  firebaseSegments: { [s: string]: ISegment };
  whitelabelUrl?: string;
  enableOnlySmsPost?: boolean;
}

interface IExtendedPost extends IPost {
  repository: PostType;
}

interface State {
  isLoaded: boolean;
  postList: Array<IExtendedPost>;
  showPostList: Array<IExtendedPost>;
  totalDonors: number;
  listCharityId?: string;
  showContactsProcessingBanner: boolean;
}

class PostList extends React.PureComponent<Props, State> {
  state = {
    isLoaded: false,
    postList: [],
    showPostList: [],
    totalDonors: 0,
    listCharityId: undefined,
    showContactsProcessingBanner: false,
  };

  async componentDidMount() {
    this.props.hideSelection();
    this.refreshReindexBanner();
    if (this.props.config) {
      let totalDonors = await paginateDonors(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        0,
        20,
        "ACCOUNT",
        true,
        this.props.serviceAccessToken,
        null,
        null,
        null,
        false
      );
      this.setState({
        totalDonors: totalDonors.totalAmount,
      });
    }
  }

  async componentDidUpdate(prevProps) {
    if (!isequal(prevProps.config, this.props.config)) {
      this.refreshReindexBanner();
      let totalDonors = await paginateDonors(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        0,
        20,
        "ACCOUNT",
        true,
        this.props.serviceAccessToken,
        null,
        null,
        null,
        false
      );
      this.setState({
        totalDonors: totalDonors.totalAmount,
      });
    }

    if (prevProps.currentCharity?.id !== this.props.currentCharity?.id) {
      this.refreshReindexBanner();
    }

    const { pendingPosts, publishedPosts } = this.props;
    let newPostList = [];
    if (isLoaded(pendingPosts) && isLoaded(publishedPosts)) {
      if (publishedPosts) {
        publishedPosts.forEach(item => {
          const value = {
            repository: "published",
            id: item.key,
            ...item.value,
          };
          if (!newPostList.some(post => post.id === value.id)) {
            newPostList.push(value);
          }
        });
      }

      if (pendingPosts) {
        pendingPosts.forEach(item => {
          const value = {
            repository: "pending",
            id: item.key,
            ...item.value,
          };
          if (!newPostList.some(post => post.id === value.id)) {
            newPostList.push(value);
          }
        });
      }

      if (this.props.sortBy.colName === "createdOn") {
        newPostList.sort((a, b) => {
          if (!a.createdOn) {
            return -1;
          } else if (!b.createdOn) {
            return 1;
          } else if (a.createdOn === b.createdOn) {
            return 0;
          } else if (a.createdOn < b.createdOn) {
            return -1;
          } else {
            return 1;
          }
        });
      } else if (this.props.sortBy.colName === "status") {
        newPostList.sort(function(a, b) {
          if (a.status === b.status) {
            return 0;
          } else if (a.status < b.status) {
            return -1;
          } else {
            return 1;
          }
        });
      } else {
        newPostList.sort((a, b) => {
          if (!a.scheduleTime) {
            return -1;
          } else if (!b.scheduleTime) {
            return 1;
          } else if (a.scheduleTime === b.scheduleTime) {
            return 0;
          } else if (a.scheduleTime < b.scheduleTime) {
            return -1;
          } else {
            return 1;
          }
        });
      }

      if (!this.props.sortAsc) {
        newPostList.reverse();
      }
    }
    newPostList = newPostList.slice(0, this.props.postCount);

    if (
      this.props.sortAsc !== prevProps.sortAsc ||
      this.props.sortBy !== prevProps.sortBy ||
      this.props.pendingPosts !== prevProps.pendingPosts ||
      this.props.publishedPosts !== prevProps.publishedPosts
    ) {
      const charityId = this.props.currentCharity && this.props.currentCharity.id;
      const keepLoadedList =
        newPostList.length === 0 &&
        this.state.postList.length > 0 &&
        this.state.listCharityId === charityId;
      if (!keepLoadedList) {
        this.setState(
          {
            postList: newPostList,
            showPostList: newPostList,
            isLoaded: true,
            listCharityId: charityId,
          },
          () => {
            this.props.updateShowMore(true);
          }
        );
      }
    }
    if (
      this.props.postCount !== prevProps.postCount &&
      isequal(this.props.publishedPosts, prevProps.publishedPosts) &&
      isequal(this.props.pendingPosts, prevProps.pendingPosts)
    ) {
      this.props.updateShowMore(false);
    } else if (
      !isequal(this.props.publishedPosts, prevProps.publishedPosts) ||
      !isequal(this.props.pendingPosts, prevProps.pendingPosts)
    ) {
      this.props.updateShowMore(true);
    }
  }

  refreshReindexBanner = async () => {
    const searchUrl = this.props.config?.searchServiceUrl;
    const charityId = this.props.currentCharity?.id;
    if (!searchUrl || !charityId) {
      this.setState({ showContactsProcessingBanner: false });
      return;
    }
    const status = await getReindexStatus(searchUrl, charityId);
    this.setState({
      showContactsProcessingBanner:
        status?.donors === true ||
        status?.segments === true ||
        status?.blockingSegmentationPostSend === true,
    });
  };

  getAuthor(post: IPost) {
    if (this.props.users) {
      const users = this.props.users.filter(user => user.key === post.authorId);
      return users.length > 0 ? users[0] : null;
    }
    return null;
  }

  getSegmentsForPost = segmentsList => {
    let segments = [];
    for (let key in segmentsList) {
      const segment = this.props.firebaseSegments[key];
      if (segment) {
        segment.id = key;
        segments.push(segment);
      }
    }
    segments.sort();
    return segments;
  };

  renderProcessingBanner() {
    if (!this.state.showContactsProcessingBanner) {
      return null;
    }
    return (
      <UncontrolledAlert color="info" className={s.processingBanner}>
        <ContactsProcessingBannerNotice linkClassName={s.processingBannerLink} />
      </UncontrolledAlert>
    );
  }

  render() {
    const pendingById: { [id: string]: IPost } = {};
    if (Array.isArray(this.props.pendingPosts)) {
      this.props.pendingPosts.forEach(item => {
        pendingById[item.key] = item.value;
      });
    }

    if (this.state.postList && this.state.postList.length > 0) {
      return (
        <Fragment>
          {this.renderProcessingBanner()}
          {this.state.showPostList.map((post: IExtendedPost, index) => {
            const deletedSegments = Object.keys({
              ...post.segments,
              ...post.excludeSegments,
            }).filter(key => !(key in this.props.firebaseSegments)).length;
            return (
              <PostItem
                key={post.id}
                index={index}
                post={post}
                postType={post.repository}
                mediaUpdating={
                  post.repository === "published" &&
                  isDeliveredPostMediaUpdating(post, pendingById[post.id])
                }
                segments={this.getSegmentsForPost(post.segments)}
                excludeSegments={this.getSegmentsForPost(post.excludeSegments)}
                deletedSegments={deletedSegments}
                user={this.getAuthor(post)}
                totalDonors={this.state.totalDonors}
                whitelabelUrl={this.props.whitelabelUrl}
                enableOnlySmsPost={this.props.enableOnlySmsPost}
              />
            );
          })}
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          {this.renderProcessingBanner()}
          <div className="d-flex full-width justify-content-center m-t-200 m-b-300">
            <ScaleLoader color={"#1f65b1"} loading />
          </div>
        </Fragment>
      );
    }
  }
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  let newPublishedPosts = get(
    firebase,
    `ordered.${user.currentCharity.id}.publishedPosts`
  );
  let newPendingPosts = get(
    firebase,
    `ordered.${user.currentCharity.id}.pendingPosts`
  );
  if (!Array.isArray(newPendingPosts)) {
    newPendingPosts = undefined;
  }
  if (!Array.isArray(newPublishedPosts)) {
    newPublishedPosts = undefined;
  }
  const segments = get(firebase, `data.${user.currentCharity.id}.segments`);
  let newUsers = get(firebase, `ordered.${user.currentCharity.id}.users`);
  const whitelabelUrl = get(
    firebase,
    `data.${user.currentCharity.id}.config.whitelabelUrl`
  );
  const enableOnlySmsPost = get(
    firebase,
    `data.${user.currentCharity.id}.config.enableOnlySmsPost`,
    false
  );
  return {
    publishedPosts: newPublishedPosts ? newPublishedPosts : null,
    pendingPosts: newPendingPosts ? newPendingPosts : null,
    users: newUsers ? newUsers : null,
    firebaseSegments: !segments ? [] : segments,
    whitelabelUrl,
    enableOnlySmsPost: Boolean(enableOnlySmsPost),
  };
};

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

export default serviceCaller(
  withLoginRedirect(
    connect<Props>(
      mapStateToProps,
      mapDispatchToProps
    )(
      firebaseConnect((props, store) => {
        return store.getState().user.currentCharity.id
          ? [
              {
                path: `${
                  store.getState().user.currentCharity.id
                }/publishedPosts`,
                queryParams: [
                  `limitToLast=${props.postCount}`,
                  "orderByChild=scheduleTime",
                ],
              },
              {
                path: `${store.getState().user.currentCharity.id}/pendingPosts`,
                queryParams: [
                  `limitToLast=${props.postCount}`,
                  "orderByChild=scheduleTime",
                ],
              },
              `${store.getState().user.currentCharity.id}/users`,
              `${store.getState().user.currentCharity.id}/segments`,
              `${store.getState().user.currentCharity.id}/config/whitelabelUrl`,
              `${store.getState().user.currentCharity.id}/config/enableOnlySmsPost`,
            ]
          : [];
      })(withStyles(s)(PostList))
    )
  )
);
